import { ClientI18NProvider } from '@susu/i18n/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useEffect, useMemo, useRef } from 'react'

import Avo from '../../Avo/Avo'
import {
  datadogLog,
  datadogLogPageView,
  flowProperties,
  initDataDog,
  initializeAnalytics,
  loadAppInsights,
  syncSystemProperties,
} from '../lib/analytics'
import { FeatureFlagsProvider } from '../lib/feature-flags'
import { translations } from '../locales'
import {
  Cancel,
  Confirmation,
  EmployeesList,
  PreAppointmentPurpose,
  StoreLocator,
  Timeslots,
  UserData,
} from '../screens'
import { getPreAppointmentPurpose } from '../screens/PreAppointmentPurpose/api/pre-appointment-api'
import { prefillUserData } from '../screens/UserData/api/user-data-api'
import {
  propsToFlowType,
  propsToHostFields,
  propsToStoreSeed,
} from './BookingToolRoot.utils'
import type { BookingToolRootProps } from './BookingToolRoot.utils'
import { useBookingStore } from '../store'

export type { BookingToolRootProps }

const QUERY_CLIENT_OPTIONS = {
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
    mutations: {
      retry: 0,
    },
  },
}

const BookingToolRootInner = (props: BookingToolRootProps) => {
  const {
    env,
    language,
    location,
    storeId,
    locationId,
    locationCode,
    employeeId,
    bookingId,
    initialScreen,
    serviceId,
    userId,
    signature,
    pageType,
    isStandalone,
    onClose,
  } = props

  // Apply deep-link props synchronously before the first paint so we never
  // briefly mount StoreLocator (and fire countrieswithstores) on reschedule/cancel.
  // `flowId` is seeded in the same block — once per real mount, since
  // `bootstrapped` is a ref — so every event of this booking flow (including
  // the `booking_tool_opened` below) carries the same value.
  const bootstrapped = useRef(false)
  if (!bootstrapped.current) {
    useBookingStore.getState().setState({
      ...propsToStoreSeed(props),
      flowId: crypto.randomUUID(),
    })
    bootstrapped.current = true

    // Wire Avo here, before any screen mounts: a child's own mount effect
    // (e.g. StoreLocator's `bookingToolStepViewed`) commits before a parent's
    // on first mount, so an init effect would run after the first events.
    initializeAnalytics(env)
    // First Segment call of the page load, on purpose. Calls made before
    // Segment has loaded are buffered and replayed grouped by method, in the
    // order each method was first called — so a `page` made after the first
    // `track` replays after every buffered `track` (`opened`, `step_viewed`,
    // `Experiment Viewed`) and lands behind them in the stream.
    if (isStandalone) {
      Avo.pageLoadedBookingtool({
        customDestinationPageName_: 'Booking Tool',
        localeVisited: useBookingStore.getState().locale,
      })
    }
    Avo.bookingToolOpened({
      ...flowProperties(),
      flowType: propsToFlowType(props),
    })
  }

  const {
    setState,
    screen,
    language: storeLanguage,
    userId: sessionUserId,
    signature: sessionSignature,
  } = useBookingStore()
  const datadogCleanupRef = useRef<(() => void) | null>(null)

  // Read by the unmount cleanup, which must not re-run when props change.
  const propsRef = useRef(props)
  propsRef.current = props

  /**
   * Drop the flow when the host takes us down.
   *
   * The store is a module-level singleton, so it outlives any single mount:
   * without this, reopening the tool resumes on the confirmation screen with the
   * previous customer still in the form. Hosts close by unmounting — the
   * storefront's panel renders `{isOpen && <BookingTool/>}`, custom-made drops
   * the wrapper once its panel has slid out — so unmount is the one signal that
   * covers every close path, including the ones that never reach our own close
   * button (the panel overlay, the mobile drag handle, Esc, a route change) or
   * the header's ✕ this used to be the only source of the event for (see
   * `ScreenHeader`).
   *
   * `bookingToolPanelClosed` fires here, before the reset, so it can still read
   * the screen the visitor closed from — not after a completed booking
   * (`confirmationVariant` is set once a create/reschedule succeeds and never
   * cleared before the next flow, so it still reads true from
   * `pre-appointment-purpose`) or a completed cancel (`cancelConfirmed`): both
   * are the flow finishing, not the visitor abandoning it.
   *
   * Doing it here rather than in the close handler also keeps the last screen on
   * screen while the host animates away, instead of flashing the store locator
   * on the way out.
   *
   * Host props are re-applied after the reset because `bootstrapped` is a ref:
   * StrictMode's remount reuses it, so nothing would re-seed `env` / `serviceId`
   * on the way back in.
   *
   * ponytail: StrictMode's dev-only mount→unmount→remount probe runs this
   * cleanup too, so a dev session can see one extra `bookingToolPanelClosed`
   * per real mount. `bootstrapped` staying `true` across the probe means the
   * flow itself doesn't restart, so this is a dev-only over-count, not a
   * production one — worth a real fix (e.g. a probe-aware guard) if it ever
   * pollutes a dev-mode funnel report.
   */
  useEffect(
    () => () => {
      const store = useBookingStore.getState()

      if (!store.confirmationVariant && !store.cancelConfirmed) {
        Avo.bookingToolPanelClosed(flowProperties())
      }

      store.resetState()
      store.setState(propsToStoreSeed(propsRef.current))
    },
    [],
  )

  /**
   * Fetch the signed-in customer as soon as both credentials are known, so the
   * details are in the store by the time the user reaches the user-data screen
   * several screens later.
   *
   * Watched rather than called once on mount: a host can resolve its session
   * after the tool is already open — the storefront reads `/api/auth` — and a
   * prefill that ran before the credentials landed would have skipped for good,
   * leaving a signed-in customer to type their own details. `prefillUserData`
   * no-ops without both fields, and again once it has filled the form or the
   * user has typed, so re-running it costs nothing.
   *
   * Deliberately outside the init effect below: that one is fire-and-forget
   * around analytics, and an unguarded throw in there used to abandon
   * everything after it — the lookup included.
   */
  useEffect(() => {
    if (!sessionUserId || !sessionSignature) return

    prefillUserData()
  }, [sessionUserId, sessionSignature])

  // Keep store in sync if host props change after mount. Host fields only —
  // the screen is the seed's business, so a prop change mid-flow cannot throw
  // the customer back to the entry screen.
  useEffect(() => {
    setState(
      propsToHostFields({
        env,
        language,
        location,
        storeId,
        locationId,
        locationCode,
        employeeId,
        bookingId,
        initialScreen,
        serviceId,
        userId,
        signature,
        pageType,
      }),
    )
    syncSystemProperties()
  }, [
    env,
    language,
    location,
    storeId,
    locationId,
    locationCode,
    employeeId,
    bookingId,
    initialScreen,
    serviceId,
    userId,
    signature,
    pageType,
    setState,
  ])

  // Initialization — runs once on mount
  useEffect(() => {
    let cancelled = false

    // Data fetches first, and deliberately outside the analytics chain below.
    //
    // `initializeAnalytics` (OneTrust + Segment) and `loadAppInsights` both do
    // real work that throws when a key is missing or a content blocker eats the
    // script — routine in local dev. `initApp()` is fire-and-forget, so an
    // unguarded throw in there rejects the promise and silently abandons
    // everything after it, and no request below would ever reach the network
    // tab. The customer lookup sits in its own effect above for the same reason
    // — as it did in the legacy tool, `attemptUpdateCustomer().catch(...)` with
    // its own catch and nothing upstream able to skip it.

    // Prefetch purpose options when the host did not pass a serviceId —
    // that path shows pre-appointment-purpose after create.
    if (!useBookingStore.getState().serviceId) {
      getPreAppointmentPurpose().catch(() => {
        // Non-blocking: the purpose screen shows a loader / empty state
        // if this fails; booking create must not depend on it.
      })
    }

    const initApp = async () => {
      // Analytics is best-effort: never let it take the booking flow down.
      try {
        loadAppInsights()
      } catch (error) {
        console.warn('Analytics initialization skipped:', error)
      }

      const cleanup = await initDataDog()

      if (cancelled) {
        cleanup?.()
        return
      }

      datadogCleanupRef.current = cleanup ?? null

      const state = useBookingStore.getState()

      // `bookingToolOpened` itself now fires from the bootstrap block above,
      // before this effect ever runs — see the comment there. Datadog RUM's
      // own page-view call is unaffected by that move.
      datadogLogPageView('Booking Tool')
      datadogLog('pageLoadedBookingTool', {
        storeId: state.storeId,
        userId: state.userId,
      })
    }

    initApp().catch((error) => {
      console.warn('Booking tool analytics init failed:', error)
    })

    return () => {
      cancelled = true
      datadogCleanupRef.current?.()
      datadogCleanupRef.current = null
    }
    // Intentionally empty dependency array: init runs once on mount.
    // Props changes are handled by the sibling effect above.
  }, [])

  const renderScreen = () => {
    switch (screen) {
      case 'store-locator':
        return <StoreLocator onClose={onClose} />
      case 'employees-list':
        return <EmployeesList onClose={onClose} />
      case 'timeslots':
        return <Timeslots onClose={onClose} />
      case 'user-data':
        return <UserData onClose={onClose} />
      case 'pre-appointment-purpose':
        return <PreAppointmentPurpose />
      case 'cancel':
        return <Cancel onClose={onClose} />
      case 'confirmation':
        return <Confirmation onClose={onClose} />
      default:
        return <StoreLocator onClose={onClose} />
    }
  }

  return (
    <ClientI18NProvider
      language={storeLanguage || 'en'}
      messages={translations}
    >
      <div className="booking-tool-v2 w-full h-full">{renderScreen()}</div>
    </ClientI18NProvider>
  )
}

/**
 * Root component for Booking Tool v2.
 * Provides its own QueryClient instance so it can be embedded in any host
 * without clashing with the host's React Query context.
 */
export const BookingToolRoot = (props: BookingToolRootProps) => {
  const queryClient = useMemo(() => new QueryClient(QUERY_CLIENT_OPTIONS), [])

  return (
    <QueryClientProvider client={queryClient}>
      <FeatureFlagsProvider
        env={props.env}
        onExperimentViewed={props.onExperimentViewed}
      >
        <BookingToolRootInner {...props} />
      </FeatureFlagsProvider>
    </QueryClientProvider>
  )
}

export default BookingToolRoot
